import type { InjectionKey, VNodeChild } from "vue";

/**
 * Something a parent places in the input column of the first SCAInput below it, such as a per-field
 * action button, without reaching into the input's markup.
 */
export interface InputAccessory {
  /** Classes for the input column that took the accessory; it is `position: relative` already. */
  columnClass: () => unknown;
  render: () => VNodeChild;
  /** Called once by each input below; only the first gets `true` and shows the accessory. */
  claim: () => boolean;
  release: () => void;
  /** Passed on by the input that took this accessory to the inputs nested in it. */
  nested?: InputAccessory;
}

export const SCA_INPUT_ACCESSORY: InjectionKey<InputAccessory | null> = Symbol("SCAInputAccessory");

/** An accessory that the first claiming input takes; `claimed` tells the provider whether any did. */
export function createInputAccessory(
  columnClass: InputAccessory["columnClass"],
  render: InputAccessory["render"],
  claimed: { value: boolean },
  nested?: InputAccessory,
): InputAccessory {
  return {
    columnClass,
    render,
    nested,
    claim() {
      if (claimed.value) return false;
      claimed.value = true;
      return true;
    },
    release() {
      claimed.value = false;
    },
  };
}

/** An accessory every claiming input takes, for one control repeated on each input of a group. */
export function createEveryInputAccessory(
  columnClass: InputAccessory["columnClass"],
  render: InputAccessory["render"],
): InputAccessory {
  return {
    columnClass,
    render,
    claim: () => true,
    // Nothing to release: no claim is exclusive.
    release: () => undefined,
  };
}
